import { useNeed } from '@/OneLiners';
import {
    ActionStatusHelper,
    useAppEnum,
    DOMAIN_CONFIGS,
    DomainFormHook,
    DomainHookProps,
    ActionResponse,
    useFormLifeCycle,
} from "@/Shared";


import { NeedFormViewModel } from '../ViewModels';
import { useNeedEnricher } from "./useNeedEnricher";
import { MatchingComputeService } from "@/Domains/Matching";
import { NeedModel } from "../NeedModel";
import { NeedValidationSchema } from "../NeedFormValidation";


/**
 * =====================================================
 * Hook: useNeedForm
 * =====================================================
 *
 * Description:
 * Domain-specific form hook for Need entities.
 * Handles form initialization, enrichment, submission
 * and access control logic.
 *
 * Responsibilities:
 * - Initialize form state (creation or edition mode)
 * - Resolve permissions and access rights
 * - Build and expose the NeedFormViewModel
 * - Handle create/update submission workflow
 *
 * Behavior:
 * - Fetches the current Need when editing
 * - Enriches domain data before building the form VM
 * - Delegates submission logic to a generic form handler
 * - Prevents unauthorized access via guarded redirects
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
export const useNeedForm = (props: DomainHookProps): DomainFormHook => {
    const { extraArgs, permissionHook } = props;
    const { contact } = extraArgs ?? {};
    const { domainType } = DOMAIN_CONFIGS.Need;

    /**
     * =====================================================
     * Need Domain Operations
     * =====================================================
     *
     * Exposes CRUD actions, current entity fetching,
     * and backend response states.
     */
    const needHook = useNeed();


    /**
     * =====================================================
     * Enums & Enrichment
     * =====================================================
     *
     * Enums are required for form construction.
     * Enrichment provides related data and computed fields.
     */
    const { NeedEnums } = useAppEnum();

    /**
     * Need enrichment layer.
     * Provides additional data (users, computed fields, etc.)
     * required to build the form view model.
     */
    const {
        sectorMap,
        contactMap,
        cityMap,
        enricherResponse,
        getEnrichedNeed,
    } = useNeedEnricher();


    /**
     * =====================================================
     * Combined Initialization Response
     * =====================================================
     *
     * Represents the global readiness state
     * required to initialize the form.
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            needHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: needHook.stateResponse.error ??
            enricherResponse.error
    };

    const lifeCycleHook = useFormLifeCycle({
        response,
        domainType,
        props,
        entityHook: needHook,
        buildVM: (currentEntity: NeedModel) => {
            return new NeedFormViewModel({
                enrichedNeed: getEnrichedNeed(currentEntity),
                viewEnums: NeedEnums,
                contactMap,
                sectorMap,
                cityMap,
                contact
            })
        },
        onSuccess: async (entity: NeedModel) => {
            try { MatchingComputeService.computeMatchingsBy('need', entity.id); }
            catch (error) { }
        },
    });


    return {
        // Form
        ...lifeCycleHook,
        viewEnums: NeedEnums,
        ValidationSchema: NeedValidationSchema(NeedEnums),
    }
}